import { useEffect, useState } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Switch,
    Button,
    Snackbar,
    Alert,
    Chip,
    CircularProgress,
    Skeleton,
} from "@mui/material";
import {
    Business as BusinessIcon,
    Star as PrimaryIcon,
} from "@mui/icons-material";

const UserCompanyAccessStep = ({
    mode,
    insertedUID,
    primaryCompId,
    adminUserId,
    path,
    AppRegID,
    GCompId,
    handlePrevStep,
    handleCancel,
    onSaved,
}) => {
    const [adminCompanies, setAdminCompanies] = useState([]);
    const [selectedCompIds, setSelectedCompIds] = useState(new Set([primaryCompId]));
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        if (!adminUserId || !path) return;
        const load = async () => {
            setFetchLoading(true);
            try {
                const [adminRes, grantsRes] = await Promise.all([
                    axios.get(`${path}/api/admin-companies/${adminUserId}`),
                    insertedUID
                        ? axios.get(`${path}/api/user-company-access/${insertedUID}`)
                        : Promise.resolve({ data: { data: [] } }),
                ]);

                if (adminRes.data.success) {
                    setAdminCompanies(adminRes.data.data || []);
                }

                const granted = new Set([primaryCompId]);
                (grantsRes.data.data || []).forEach(g => granted.add(g.CompId));
                setSelectedCompIds(granted);
            } catch (err) {
                console.error("Error loading company access data:", err);
            } finally {
                setFetchLoading(false);
            }
        };
        load();
    }, [adminUserId, insertedUID, primaryCompId, path]);

    const handleToggle = (compId) => {
        if (compId === primaryCompId) return;
        setSelectedCompIds(prev => {
            const next = new Set(prev);
            if (next.has(compId)) {
                next.delete(compId);
            } else {
                next.add(compId);
            }
            return next;
        });
    };

    const buildGrantedList = () => {
        const list = adminCompanies.filter(c => selectedCompIds.has(c.CompId));
        if (!list.find(c => c.CompId === primaryCompId)) {
            list.unshift({ CompId: primaryCompId, CompanyName: "Primary Company" });
        }
        return list;
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const companyIds = Array.from(selectedCompIds).filter(id => id !== primaryCompId);
            await axios.post(`${path}/api/user-company-access`, {
                AppRegID,
                GCompId,
                userId: insertedUID,
                companyIds,
                grantedByUserId: adminUserId,
            });
            onSaved(buildGrantedList());
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || "Failed to save company access",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSkip = () => {
        const primary = adminCompanies.find(c => c.CompId === primaryCompId);
        onSaved(primary ? [primary] : [{ CompId: primaryCompId, CompanyName: "Primary Company" }]);
    };

    // ──────────────── Alt+S (Save) & Alt+C (Cancel) shortcuts ────────────────
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    // Guarded by `fetchLoading`: this component early-returns to render a loading
    // skeleton instead of the real form when true, but hooks still run on every
    // render regardless — without this guard, the listener would try to save/
    // cancel a form that isn't actually showing yet.
    useEffect(() => {
        if (fetchLoading) return;

        const handleGlobalKeyDown = (e) => {
            if (!e.altKey) return;

            if (e.code === "KeyS") {
                e.preventDefault();
                if (!loading) {
                    handleSave();
                }
            } else if (e.code === "KeyC") {
                e.preventDefault();
                handleCancel();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    if (fetchLoading) {
        return (
            <Box sx={{ p: 2, maxWidth: 640, mx: "auto" }}>
                {[1, 2, 3].map(i => (
                    <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1.5 }} />
                ))}
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 640, mx: "auto" }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" fontWeight={700}>
                    Company Access
                </Typography>
                <Typography fontSize={12} color="text.secondary">
                    {selectedCompIds.size} of {adminCompanies.length} selected
                </Typography>
            </Box>

            <Typography fontSize={13} color="text.secondary" mb={2}>
                Select which companies this user can access. The primary company is always granted.
            </Typography>

            {adminCompanies.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                    <BusinessIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography fontSize={14}>No additional companies available to grant.</Typography>
                </Box>
            ) : (
                adminCompanies.map((company) => {
                    const isPrimary = company.CompId === primaryCompId;
                    const isSelected = selectedCompIds.has(company.CompId);
                    return (
                        <Card
                            key={company.CompId}
                            variant="outlined"
                            sx={{
                                mb: 1.5,
                                borderRadius: 2,
                                borderColor: isSelected ? "#3b82f6" : "#e2e8f0",
                                bgcolor: isSelected ? "#eff6ff" : "#fff",
                                transition: "all 0.15s",
                                cursor: isPrimary ? "default" : "pointer",
                            }}
                            onClick={() => !isPrimary && handleToggle(company.CompId)}
                        >
                            <CardContent sx={{ py: "12px !important", px: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <BusinessIcon sx={{ fontSize: 20, color: isSelected ? "#3b82f6" : "#94a3b8" }} />
                                    <Box>
                                        <Typography fontSize={14} fontWeight={600} color={isSelected ? "#1e40af" : "text.primary"}>
                                            {company.CompanyName}
                                        </Typography>
                                        {company.City && (
                                            <Typography fontSize={12} color="text.secondary">
                                                {company.City}{company.GSTNo ? ` · GST: ${company.GSTNo}` : ""}
                                            </Typography>
                                        )}
                                    </Box>
                                    {isPrimary && (
                                        <Chip
                                            icon={<PrimaryIcon sx={{ fontSize: 14 }} />}
                                            label="Primary"
                                            size="small"
                                            color="primary"
                                            variant="filled"
                                            sx={{ fontSize: 11, height: 22 }}
                                        />
                                    )}
                                </Box>
                                <Switch
                                    checked={isSelected}
                                    disabled={isPrimary}
                                    onChange={(e) => { e.stopPropagation(); handleToggle(company.CompId); }}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#1d4ed8" },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#3b82f6" },
                                    }}
                                />
                            </CardContent>
                        </Card>
                    );
                })
            )}

            <Box display="flex" gap={1.5} mt={3}>
                <Button variant="outlined" onClick={handlePrevStep} sx={{ flex: 1 }}>
                    Back
                </Button>
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleSkip}
                    sx={{ flex: 1, color: "text.secondary" }}
                >
                    Skip
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ flex: 1 }}
                >
                    {loading ? "Saving…" : "Save & Continue"}
                </Button>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default UserCompanyAccessStep;
